import React from 'react';
import { BookOpen, CheckCircle2, Calendar, Euro, MapPin } from 'lucide-react';
import { NgapAct } from '../types/ngap';
import { POINT_VALUE_METROPOLE, POINT_VALUE_DROM, formatTarif } from '../utils/pricingUtils';

interface SeoContentBlockProps {
  act: NgapAct;
  darkMode: boolean;
}

const REVALO_LABELS: Record<string, string> = {
  janv_2026: '1er janvier 2026',
  may_2026: '28 mai 2026',
  sept_2026: '1er septembre 2026',
  july_2027: '1er juillet 2027',
};

const getTodayStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Retourne la valeur de cotation actuellement en vigueur (en tenant compte des revalorisations passées)
const getEffectiveCotationValue = (act: NgapAct): string => {
  const todayStr = getTodayStr();
  if (act.revalo_july_2027 && act.cotation_valeur_july_2027 && todayStr >= '2027-07-01') {
    return act.cotation_valeur_july_2027;
  }
  if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026 && todayStr >= '2026-09-01') {
    return act.cotation_valeur_sept_2026;
  }
  if (act.revalo_may_2026 && act.cotation_valeur_may_2026 && todayStr >= '2026-05-28') {
    return act.cotation_valeur_may_2026;
  }
  if (act.revalo_janv_2026 && act.cotation_valeur_janv_2026 && todayStr >= '2026-01-01') {
    return act.cotation_valeur_janv_2026;
  }
  return act.cotation_valeur;
};

const SeoContentBlock: React.FC<SeoContentBlockProps> = ({ act, darkMode }) => {
  const letterKey = act.lettre_cle?.toUpperCase() || '';
  const cotationValue = getEffectiveCotationValue(act);
  const numericValue = parseFloat((cotationValue || '').replace(',', '.')) || 0;
  const tarifMetropole = numericValue * POINT_VALUE_METROPOLE;
  const tarifDrom = numericValue * POINT_VALUE_DROM;

  const todayStr = getTodayStr();
  const revalorisations: { key: string; label: string; value: string }[] = [];
  if (act.revalo_janv_2026 && act.cotation_valeur_janv_2026 && todayStr < '2026-01-01') {
    revalorisations.push({ key: 'janv_2026', label: REVALO_LABELS.janv_2026, value: act.cotation_valeur_janv_2026 });
  }
  if (act.revalo_may_2026 && act.cotation_valeur_may_2026 && todayStr < '2026-05-28') {
    revalorisations.push({ key: 'may_2026', label: REVALO_LABELS.may_2026, value: act.cotation_valeur_may_2026 });
  }
  if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026 && todayStr < '2026-09-01') {
    revalorisations.push({ key: 'sept_2026', label: REVALO_LABELS.sept_2026, value: act.cotation_valeur_sept_2026 });
  }
  if (act.revalo_july_2027 && act.cotation_valeur_july_2027 && todayStr < '2027-07-01') {
    revalorisations.push({ key: 'july_2027', label: REVALO_LABELS.july_2027, value: act.cotation_valeur_july_2027 });
  }

  const pathologies = (act.pathologies || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textBase = darkMode ? 'text-gray-300' : 'text-gray-700';
  const heading = darkMode ? 'text-white' : 'text-gray-900';

  return (
    <section
      aria-labelledby="seo-content-heading"
      className={`rounded-xl border p-6 shadow-sm ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-center mb-4">
        <BookOpen className={`h-6 w-6 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
        <h2 id="seo-content-heading" className={`text-xl font-bold ${heading}`}>
          À propos de la cotation {letterKey} {cotationValue}
        </h2>
      </div>

      <div className={`space-y-4 leading-relaxed ${textBase}`}>
        <p>
          La cotation <strong>{letterKey} {cotationValue}</strong> correspond à l'acte
          {' '}<em>{act.libelle}</em>
          {act.theme ? <> dans le domaine <strong>{act.theme}</strong></> : null}
          . Elle fait partie de la Nomenclature Générale des Actes Professionnels (NGAP)
          appliquée aux masseurs-kinésithérapeutes conventionnés.
        </p>

        {/* Tarifs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className={`p-3 rounded-lg flex items-center ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <MapPin className={`h-5 w-5 mr-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <div className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>
                Métropole
              </div>
              <div className={`text-lg font-bold ${heading}`}>
                <Euro className="h-4 w-4 inline mr-1" />
                {formatTarif(tarifMetropole)}
              </div>
            </div>
          </div>
          <div className={`p-3 rounded-lg flex items-center ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <MapPin className={`h-5 w-5 mr-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <div className={`text-xs font-semibold uppercase tracking-wide ${textMuted}`}>
                DROM
              </div>
              <div className={`text-lg font-bold ${heading}`}>
                <Euro className="h-4 w-4 inline mr-1" />
                {formatTarif(tarifDrom)}
              </div>
            </div>
          </div>
        </div>

        {/* Référentiel */}
        <p>
          <CheckCircle2
            className={`h-4 w-4 inline mr-1 ${
              act.soumis_referentiel ? 'text-green-500' : darkMode ? 'text-gray-500' : 'text-gray-400'
            }`}
          />
          {act.soumis_referentiel
            ? `Cet acte est soumis au référentiel. Tu dois respecter les conditions d'application prévues par la convention.`
            : `Cet acte n'est pas soumis au référentiel. Il peut être coté sans limitation spécifique prévue par un référentiel conventionnel.`}
        </p>

        {/* Nombre de séances */}
        {act.number_seances ? (
          <p>
            Le nombre de séances associé à cet acte est de <strong>{act.number_seances}</strong>.
          </p>
        ) : null}

        {/* Demande d'accord préalable */}
        {act.demande_accord_prealable ? (
          <p>
            <strong>Demande d'accord préalable :</strong> {act.demande_accord_prealable}.
          </p>
        ) : null}

        {/* Revalorisations */}
        {revalorisations.length > 0 && (
          <div>
            <h3 className={`text-base font-semibold mb-2 flex items-center ${heading}`}>
              <Calendar className={`h-4 w-4 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              Revalorisations prévues
            </h3>
            <ul className={`list-disc pl-5 space-y-1 ${textBase}`}>
              {revalorisations.map((r) => (
                <li key={r.key}>
                  <strong>{r.label} :</strong> {letterKey} {r.value}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pathologies */}
        {pathologies.length > 0 && (
          <div>
            <h3 className={`text-base font-semibold mb-2 ${heading}`}>
              Pathologies associées
            </h3>
            <div className="flex flex-wrap gap-2">
              {pathologies.map((p) => (
                <span
                  key={p}
                  className={`px-2 py-1 text-xs rounded-md ${
                    darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  #{p}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        {act.tips && (
          <div className={`p-3 rounded-lg border-l-4 ${
            darkMode ? 'bg-blue-900/20 border-blue-500 text-blue-200' : 'bg-blue-50 border-blue-500 text-blue-800'
          }`}>
            <strong>Bon à savoir :</strong> {act.tips}
          </div>
        )}
      </div>
    </section>
  );
};

export default React.memo(SeoContentBlock);
